import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Clock, Plus, Trash2, Save, Check, X,
  MapPin, AlertCircle, Filter
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { useAdmin } from '@/contexts/AdminContext';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Map UI Index (0=Mon...6=Sun) to Backend Index (0=Sun, 1=Mon...6=Sat)
const uiToDbDay = (uiIdx) => (uiIdx === 6 ? 0 : uiIdx + 1);
const dbToUiDay = (dbIdx) => (dbIdx === 0 ? 6 : dbIdx - 1);

// Generate discrete 15-min slots between start and end times
function generateSlots(start, end) {
  const slots = [];
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let cur = sh * 60 + sm;
  const endM = eh * 60 + em;
  while (cur < endM) {
    const h = Math.floor(cur / 60);
    const m = cur % 60;
    slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    cur += 15;
  }
  return slots;
}

// Collapse discrete 15-min slots back into contiguous intervals
function collapseSlots(slots) {
  if (!slots || slots.length === 0) return [];
  const sorted = [...slots].sort();
  const intervals = [];
  let currentStart = sorted[0];
  let currentEnd = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    const slot = sorted[i];
    const [ch, cm] = currentEnd.split(':').map(Number);
    const expectedNext = ch * 60 + cm + 15;
    const [sh, sm] = slot.split(':').map(Number);
    if (sh * 60 + sm === expectedNext) {
      currentEnd = slot;
    } else {
      const [eh, em] = currentEnd.split(':').map(Number);
      const endMins = eh * 60 + em + 15;
      intervals.push({ start: currentStart, end: `${String(Math.floor(endMins / 60)).padStart(2, '0')}:${String(endMins % 60).padStart(2, '0')}` });
      currentStart = slot;
      currentEnd = slot;
    }
  }
  const [eh, em] = currentEnd.split(':').map(Number);
  const endMins = eh * 60 + em + 15;
  intervals.push({ start: currentStart, end: `${String(Math.floor(endMins / 60)).padStart(2, '0')}:${String(endMins % 60).padStart(2, '0')}` });
  return intervals;
}

const SITES = {
  'shihab_romeed': 'Shihab Romeed (SR)',
  'specialist_dental_london': 'Specialist Dental London (SDL)',
  'london_dental_academy': 'London Dental Academy (LDA)'
};

const SERVICE_MODES = [
  { value: null, label: 'All Services', desc: 'These hours apply to every service' },
  { value: 'only', label: 'Only These Services', desc: 'Restrict these hours to specific services only' },
  { value: 'except', label: 'All Services Except', desc: 'These hours apply to everything except selected services' },
];

/* ── Weekly Hours Editor ────────────────────── */
function WeeklyHoursEditor({ siteKey, schedules, services, onSave, saving }) {
  const [hours, setHours] = useState(() => {
    const result = DAY_KEYS.reduce((acc, d) => ({ ...acc, [d]: { intervals: [], serviceMode: null, serviceIds: [] } }), {});
    if (Array.isArray(schedules)) {
      schedules.filter(s => s.siteKey === siteKey).forEach(s => {
        const uiIdx = dbToUiDay(s.dayOfWeek);
        const dayKey = DAY_KEYS[uiIdx];
        if (dayKey && Array.isArray(s.timeSlots)) {
          // Handle old range format ("09:00-17:00")
          let slots = [];
          for (const t of s.timeSlots) {
            if (t.includes('-')) {
              slots.push(...generateSlots(t.split('-')[0], t.split('-')[1]));
            } else {
              slots.push(t);
            }
          }
          result[dayKey] = {
            intervals: collapseSlots(slots),
            serviceMode: s.serviceMode || null,
            serviceIds: s.serviceIds || [],
          };
        }
      });
    }
    const isEmpty = Object.values(result).every(d => d.intervals.length === 0);
    if (isEmpty) {
      for (let i = 0; i < 5; i++) result[DAY_KEYS[i]] = { intervals: [{ start: '09:00', end: '17:00' }], serviceMode: null, serviceIds: [] };
    }
    return result;
  });

  const addInterval = (day) => setHours(p => ({
    ...p, [day]: { ...p[day], intervals: [...p[day].intervals, { start: '09:00', end: '17:00' }] }
  }));
  const removeInterval = (day, idx) => setHours(p => ({
    ...p, [day]: { ...p[day], intervals: p[day].intervals.filter((_, i) => i !== idx) }
  }));
  const updateInterval = (day, idx, field, value) => setHours(p => ({
    ...p, [day]: { ...p[day], intervals: p[day].intervals.map((s, i) => i === idx ? { ...s, [field]: value } : s) }
  }));
  const setDayEnabled = (day, enabled) => setHours(p => ({
    ...p, [day]: { ...p[day], intervals: enabled ? [{ start: '09:00', end: '17:00' }] : [] }
  }));
  const setServiceMode = (day, mode) => setHours(p => ({
    ...p, [day]: { ...p[day], serviceMode: mode, serviceIds: mode === null ? [] : p[day].serviceIds }
  }));
  const toggleServiceId = (day, sid) => setHours(p => {
    const current = p[day].serviceIds;
    const next = current.includes(sid) ? current.filter(i => i !== sid) : [...current, sid];
    return { ...p, [day]: { ...p[day], serviceIds: next } };
  });

  const handleSaveClick = () => {
    const newSchedules = [];
    DAY_KEYS.forEach((day, uiIdx) => {
      const { intervals, serviceMode, serviceIds } = hours[day];
      if (intervals.length > 0) {
        let allSlots = [];
        for (const interval of intervals) {
          if (interval.start && interval.end) {
            allSlots = allSlots.concat(generateSlots(interval.start, interval.end));
          }
        }
        allSlots = [...new Set(allSlots)].sort();
        if (allSlots.length > 0) {
          newSchedules.push({
            siteKey,
            dayOfWeek: uiToDbDay(uiIdx),
            timeSlots: allSlots,
            isAvailable: true,
            serviceMode: serviceMode || null,
            serviceIds: serviceMode ? serviceIds : [],
          });
        }
      }
    });
    onSave(newSchedules);
  };

  const siteServices = services.filter(s => s.siteKeys?.includes(siteKey));

  return (
    <div className="bg-white rounded-[1.5rem] border border-stone-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between p-5 border-b border-stone-100 bg-[#FCFBF9] gap-4">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white border border-stone-100 shadow-sm">
            <Clock className="w-5 h-5 text-[#C8A86B]" />
          </div>
          <div>
            <h3 className="font-heading text-xl text-stone-900 tracking-tight">Weekly Schedule</h3>
            <p className="text-xs text-stone-500 font-body mt-0.5">Define availability for {SITES[siteKey]}</p>
          </div>
        </div>
        <Button onClick={handleSaveClick} disabled={saving} className="h-11 gap-2 bg-stone-900 hover:bg-stone-800 text-white px-7 rounded-full w-full sm:w-auto shadow-sm">
          <Save className="w-4 h-4" />{saving ? 'Saving...' : 'Save Schedule'}
        </Button>
      </div>

      {/* Days List */}
      <div className="divide-y divide-stone-100">
        {DAY_KEYS.map((day, di) => {
          const dayData = hours[day];
          const hasIntervals = dayData.intervals.length > 0;
          return (
            <div key={day} className={`p-5 transition-colors ${hasIntervals ? 'bg-white' : 'bg-stone-50/40'}`}>
              <div className="flex flex-col lg:flex-row gap-5 lg:items-start">
                
                {/* Day Label & Toggle */}
                <div className="w-40 shrink-0 flex items-center gap-3 pt-1">
                  <Switch checked={hasIntervals} onCheckedChange={(c) => setDayEnabled(day, c)} className="data-[state=checked]:bg-[#C8A86B]" />
                  <span className={cn("text-[15px] font-heading font-medium tracking-wide", hasIntervals ? "text-stone-900" : "text-stone-400")}>
                    {DAY_LABELS[di]}
                  </span>
                </div>

                {/* Intervals & Filters */}
                <div className="flex-1 space-y-4">
                  <AnimatePresence mode="popLayout">
                    {!hasIntervals ? (
                      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-sm text-stone-400 italic py-1 font-body">
                        Not available on {DAY_LABELS[di]}s
                      </motion.p>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex flex-col gap-2.5">
                          {dayData.intervals.map((slot, si) => (
                            <motion.div key={si} initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} className="flex flex-wrap items-center gap-2 group">
                              <div className="flex items-center bg-stone-50 rounded-xl p-1.5 border border-stone-200 focus-within:ring-2 focus-within:ring-[#C8A86B] focus-within:border-transparent transition-all shadow-sm">
                                <input type="time" step="900" value={slot.start} onChange={e => updateInterval(day, si, 'start', e.target.value)} className="h-9 w-[100px] text-sm font-medium bg-transparent text-center focus:outline-none text-stone-700 cursor-pointer" />
                                <span className="text-stone-300 font-medium px-2">-</span>
                                <input type="time" step="900" value={slot.end} onChange={e => updateInterval(day, si, 'end', e.target.value)} className="h-9 w-[100px] text-sm font-medium bg-transparent text-center focus:outline-none text-stone-700 cursor-pointer" />
                              </div>
                              <button onClick={() => removeInterval(day, si)} className="h-12 w-12 rounded-xl flex items-center justify-center text-stone-400 hover:text-red-500 hover:bg-red-50 transition-colors lg:opacity-0 lg:group-hover:opacity-100">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </motion.div>
                          ))}
                        </div>
                        
                        <button onClick={() => addInterval(day)} className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-semibold text-stone-400 hover:text-[#C8A86B] transition-colors py-1">
                          <Plus className="w-3.5 h-3.5" /> Add Hours
                        </button>
                      </div>
                    )}
                  </AnimatePresence>

                  {/* Service Filters */}
                  {hasIntervals && siteServices.length > 0 && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 pt-4 border-t border-stone-100">
                      <div className="flex items-center gap-2 mb-3">
                        <Filter className="w-3.5 h-3.5 text-[#C8A86B]" />
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Service Restrictions</span>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {SERVICE_MODES.map(m => (
                          <button
                            key={String(m.value)}
                            onClick={() => setServiceMode(day, m.value)}
                            className={cn(
                              "px-4 py-2 rounded-full text-xs font-medium border transition-all",
                              dayData.serviceMode === m.value
                                ? "bg-stone-900 text-white border-stone-900 shadow-md"
                                : "bg-white text-stone-600 border-stone-200 hover:bg-stone-50"
                            )}
                          >
                            {m.label}
                          </button>
                        ))}
                      </div>
                      
                      <AnimatePresence>
                        {dayData.serviceMode && (
                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="flex flex-wrap gap-2 overflow-hidden">
                            {siteServices.map(s => {
                              const isSelected = dayData.serviceIds.includes(s.id);
                              return (
                                <button
                                  key={s.id}
                                  onClick={() => toggleServiceId(day, s.id)}
                                  className={cn(
                                    "px-3 py-2 rounded-xl text-xs font-medium border transition-all flex items-center gap-2",
                                    isSelected
                                      ? "bg-[#C8A86B]/10 text-stone-900 border-[#C8A86B]"
                                      : "bg-white text-stone-500 border-stone-200 hover:border-stone-300"
                                  )}
                                >
                                  <div className={cn("w-4 h-4 rounded-[4px] border flex items-center justify-center transition-colors", isSelected ? "bg-[#C8A86B] border-[#C8A86B]" : "bg-white border-stone-300")}>
                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                  </div>
                                  {s.name}
                                </button>
                              );
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Main Page ─────────────────────────────── */
export default function ProviderSchedule() {
  const { id } = useParams();
  const { apiFetch } = useAdmin();
  const [provider, setProvider] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSite, setActiveSite] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [provRes, svcRes] = await Promise.all([
          apiFetch(`/providers/${id}`),
          apiFetch('/services?allStatuses=true')
        ]);
        const provData = await provRes.json();
        const svcData = await svcRes.json();
        const prov = provData.provider || provData;
        setProvider(prov);
        setServices(svcData.services || []);
        if (prov.sites?.length > 0) setActiveSite(prov.sites[0]);
      } catch (err) {
        toast.error('Failed to fetch provider');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, apiFetch]);

  const handleSaveHours = async (newSiteSchedules) => {
    setSaving(true);
    try {
      const otherSiteSchedules = (provider.schedules || [])
        .filter(s => s.siteKey !== activeSite)
        .map(s => ({
          siteKey: s.siteKey,
          dayOfWeek: s.dayOfWeek,
          timeSlots: s.timeSlots,
          isAvailable: s.isAvailable,
          serviceMode: s.serviceMode || null,
          serviceIds: s.serviceIds || [],
        }));
      const allSchedules = [...otherSiteSchedules, ...newSiteSchedules];

      const res = await apiFetch(`/providers/${id}/schedule`, {
        method: 'PUT',
        body: { schedules: allSchedules },
      });
      if (!res.ok) throw new Error('Failed to save schedule');
      toast.success('Schedule saved successfully');
      setProvider(p => ({ ...p, schedules: allSchedules }));
    } catch (err) {
      toast.error(err.message || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[600px] rounded-2xl" />
      </div>
    );
  }

  const providerSites = provider?.sites || [];

  return (
    <div className="max-w-4xl pb-20">
      <div className="flex items-center gap-4 mb-8">
        <Link to="/admin/schedule" className="h-10 w-10 rounded-full border border-stone-200 bg-white flex items-center justify-center hover:bg-stone-50 transition-colors shadow-sm">
          <ArrowLeft className="w-4 h-4 text-stone-600" />
        </Link>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-400">Scheduling</p>
          <h2 className="text-2xl md:text-3xl font-playfair font-medium text-stone-900 tracking-tight">
            {provider?.name || 'Provider'}
          </h2>
        </div>
      </div>

      {providerSites.length === 0 ? (
        <Card className="border-stone-200 shadow-sm">
          <CardContent className="p-12 text-center">
            <AlertCircle className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-stone-900">No Sites Assigned</h3>
            <p className="text-stone-500 mt-2 max-w-sm mx-auto">This provider is not assigned to any booking portals. Please update their profile first.</p>
            <Link to={`/admin/providers/${id}/edit`}>
              <Button className="mt-6">Edit Profile</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          <div className="flex gap-2 p-1 bg-stone-100 rounded-xl w-fit">
            {providerSites.map(site => (
              <button
                key={site}
                onClick={() => setActiveSite(site)}
                className={cn(
                  "px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2",
                  activeSite === site ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-700 hover:bg-stone-200/50"
                )}
              >
                <MapPin className="w-3.5 h-3.5" /> {SITES[site] || site}
              </button>
            ))}
          </div>

          <div className="mt-4">
            {activeSite && (
              <WeeklyHoursEditor
                key={activeSite}
                siteKey={activeSite}
                schedules={provider.schedules}
                services={services}
                onSave={handleSaveHours}
                saving={saving}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
